import { useState } from 'react';
import type { JobApplication } from '@shared/types';
import { STATUS_LABELS, STATUS_COLORS } from '@shared/constants';

interface ApplicationDetailModalProps {
  app: JobApplication;
  onClose: () => void;
  onSaveJd: (appId: string, description: string) => Promise<void>;
  onAnalyze: (appId: string) => Promise<void>;
  onFullDetails: (appId: string) => void;
}


export default function ApplicationDetailModal({ app, onClose, onSaveJd, onAnalyze, onFullDetails }: ApplicationDetailModalProps) {
  const [jdExpanded, setJdExpanded] = useState(false);
  const [editingJd, setEditingJd] = useState(false);
  const [jdText, setJdText] = useState(app.description || '');
  const [savingJd, setSavingJd] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600 dark:text-green-400';
    if (score >= 60) return 'text-blue-600 dark:text-blue-400';
    if (score >= 40) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  const getScoreBgColor = (score: number) => {
    if (score >= 80) return 'bg-green-50 dark:bg-green-900/10';
    if (score >= 60) return 'bg-blue-50 dark:bg-blue-900/10';
    if (score >= 40) return 'bg-yellow-50 dark:bg-yellow-900/10';
    return 'bg-red-50 dark:bg-red-900/10';
  };

  const handleSaveJd = async () => {
    if (!app.id) return;
    setSavingJd(true);
    try {
      await onSaveJd(app.id, jdText);
      setEditingJd(false);
    } catch (e) {
      console.error(e);
    } finally {
      setSavingJd(false);
    }
  };

  const handleAnalyze = async () => {
    if (!app.id) return;
    setAnalyzing(true);
    try {
      await onAnalyze(app.id);
    } catch (e) {
      console.error(e);
    } finally {
      setAnalyzing(false);
    }
  };

  const hasJd = app.description && app.description.length >= 50;
  const hasResume = !!app.matchedResumeId;
  const canAnalyze = hasJd && !analyzing;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-start">
          <div className="min-w-0 flex-1">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white truncate">{app.jobTitle}</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">{app.company}</p>
            {app.location && <p className="text-xs text-gray-500 dark:text-gray-500">{app.location}</p>}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => app.id && onFullDetails(app.id)}
              className="text-xs px-3 py-1 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-700 rounded-lg hover:bg-indigo-100 transition-colors"
            >
              Full Details
            </button>
            <span className={`text-xs px-2 py-1 rounded ${STATUS_COLORS[app.status]}`}>
              {STATUS_LABELS[app.status]}
            </span>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-gray-500 dark:text-gray-400">Applied</span>
              <p className="font-medium text-gray-900 dark:text-white">
                {(() => {
                  if (!app.appliedDate) return 'N/A';
                  const d = app.appliedDate instanceof Date ? app.appliedDate : (app.appliedDate as any).toDate?.();
                  return d ? d.toLocaleDateString() : 'N/A';
                })()}
              </p>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">Source</span>
              <p className="font-medium text-gray-900 dark:text-white capitalize">{app.source}</p>
            </div>
            {app.jobUrl && (
              <div className="col-span-2">
                <span className="text-gray-500 dark:text-gray-400">Job URL</span>
                <a
                  href={app.jobUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-blue-600 dark:text-blue-400 hover:underline truncate"
                >
                  {app.jobUrl}
                </a>
              </div>
            )}
          </div>

          {/* Job Description — Editable */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-gray-900 dark:text-white">Job Description</h3>
              <div className="flex items-center gap-2">
                {app.description && !editingJd && (
                  <button
                    onClick={() => setJdExpanded(!jdExpanded)}
                    className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    {jdExpanded ? 'Collapse' : 'Expand'}
                  </button>
                )}
                {!editingJd && (
                  <button
                    onClick={() => { setEditingJd(true); setJdText(app.description || ''); }}
                    className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    {app.description ? 'Edit' : 'Add JD'}
                  </button>
                )}
              </div>
            </div>

            {editingJd ? (
              <div>
                <textarea
                  value={jdText}
                  onChange={e => setJdText(e.target.value)}
                  placeholder="Paste the full job description here..."
                  className="w-full h-48 p-3 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 resize-y focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs text-gray-400">{jdText.length} characters</span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setEditingJd(false)}
                      className="px-3 py-1.5 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveJd}
                      disabled={savingJd || jdText.trim().length < 50}
                      className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {savingJd ? 'Saving...' : 'Save JD'}
                    </button>
                  </div>
                </div>
                {jdText.trim().length > 0 && jdText.trim().length < 50 && (
                  <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                    JD must be at least 50 characters for analysis.
                  </p>
                )}
              </div>
            ) : app.description ? (
              <div>
                <div className={`bg-gray-50 dark:bg-gray-900 rounded-lg p-3 ${jdExpanded ? 'max-h-none' : 'max-h-48'} overflow-y-auto`}>
                  <p className="text-xs text-gray-600 dark:text-gray-400 whitespace-pre-wrap">
                    {jdExpanded
                      ? app.description
                      : app.description.length > 500
                        ? app.description.substring(0, 500) + '...'
                        : app.description}
                  </p>
                </div>
                <p className="mt-1 text-xs text-gray-400">
                  {app.description.length} characters
                </p>
              </div>
            ) : (
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No job description. Click "Add JD" to paste one for analysis.
                </p>
              </div>
            )}
          </div>

          {/* Analyze Button */}
          <div className="flex items-center gap-3">
            <button
              onClick={handleAnalyze}
              disabled={!canAnalyze}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {analyzing ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Analyzing...
                </>
              ) : hasResume ? (
                'Analyze Resume'
              ) : (
                'Analyze JD'
              )}
            </button>
            {!hasJd && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Add a job description first
              </span>
            )}
            {hasJd && !hasResume && !analyzing && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Upload a resume for full match analysis
              </span>
            )}
          </div>

          {/* Match Score Section */}
          {typeof app.matchScore === 'number' && app.matchScore >= 0 ? (
            <div className={`rounded-lg p-4 ${getScoreBgColor(app.matchScore)}`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-900 dark:text-white">Match Analysis</h3>
                <span className={`text-2xl font-bold ${getScoreColor(app.matchScore)}`}>
                  {app.matchScore}%
                </span>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <span className="text-xs text-gray-500 dark:text-gray-400">Skill Match</span>
                  <p className={`text-lg font-semibold ${getScoreColor(app.skillMatchScore || 0)}`}>
                    {app.skillMatchScore || 0}%
                  </p>
                </div>
                <div>
                  <span className="text-xs text-gray-500 dark:text-gray-400">Semantic Match</span>
                  <p className={`text-lg font-semibold ${getScoreColor(app.embeddingScore || 0)}`}>
                    {app.embeddingScore || 0}%
                  </p>
                </div>
              </div>

              {/* Matched Skills */}
              {app.matchedSkills && app.matchedSkills.length > 0 && (
                <div className="mb-3">
                  <span className="text-xs font-medium text-green-700 dark:text-green-400">
                    Matched Skills ({app.matchedSkills.length})
                  </span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {app.matchedSkills.map((skill, i) => (
                      <span
                        key={i}
                        className="text-xs px-2 py-0.5 bg-green-200 dark:bg-green-800 text-green-800 dark:text-green-200 rounded"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Missing Skills */}
              {app.missingSkills && app.missingSkills.length > 0 && (
                <div>
                  <span className="text-xs font-medium text-red-700 dark:text-red-400">
                    Missing Skills ({app.missingSkills.length})
                  </span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {app.missingSkills.map((skill, i) => (
                      <span
                        key={i}
                        className="text-xs px-2 py-0.5 bg-red-200 dark:bg-red-800 text-red-800 dark:text-red-200 rounded"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : !hasResume ? (
            <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                No resume attached. Upload a resume on the job card to enable analysis.
              </p>
            </div>
          ) : null}

          {/* Keywords Section */}
          {((app.matchedKeywords && app.matchedKeywords.length > 0) || (app.missingKeywords && app.missingKeywords.length > 0)) && (
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Keywords</h3>
              <div className="space-y-2">
                {app.matchedKeywords && app.matchedKeywords.length > 0 && (
                  <div>
                    <span className="text-xs font-medium text-green-700 dark:text-green-400">
                      Matched ({app.matchedKeywords.length})
                    </span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {app.matchedKeywords.map((kw, i) => (
                        <span key={i} className="text-xs px-2 py-0.5 bg-green-200 dark:bg-green-800 text-green-800 dark:text-green-200 rounded">
                          {kw}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {app.missingKeywords && app.missingKeywords.length > 0 && (
                  <div>
                    <span className="text-xs font-medium text-orange-700 dark:text-orange-400">
                      Missing ({app.missingKeywords.length})
                    </span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {app.missingKeywords.map((kw, i) => (
                        <span key={i} className="text-xs px-2 py-0.5 bg-orange-200 dark:bg-orange-800 text-orange-800 dark:text-orange-200 rounded">
                          {kw}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* JD Analysis - Skills & Keywords from JD */}
          {app.jdSkills && app.jdSkills.length > 0 && (
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Job Requirements</h3>
              <div className="space-y-2">
                <div>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Required Skills ({app.jdSkills.length})
                  </span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {app.jdSkills.map((skill, i) => (
                      <span
                        key={i}
                        className={`text-xs px-2 py-0.5 rounded ${
                          app.matchedSkills?.includes(skill)
                            ? 'bg-green-200 dark:bg-green-800 text-green-800 dark:text-green-200'
                            : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>

                {app.jdKeywords && app.jdKeywords.length > 0 && (
                  <div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      Keywords ({app.jdKeywords.length})
                    </span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {app.jdKeywords.map((keyword, i) => (
                        <span
                          key={i}
                          className="text-xs px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200 rounded"
                        >
                          {keyword}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Suggested Phrases */}
          {app.jdSuggestedPhrases && app.jdSuggestedPhrases.length > 0 && (
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Suggested Phrases</h3>
              <ul className="space-y-1">
                {app.jdSuggestedPhrases.map((phrase, i) => (
                  <li key={i} className="text-sm text-gray-600 dark:text-gray-400 flex items-start gap-2">
                    <span className="text-blue-500 mt-0.5">&#8226;</span>
                    {phrase}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Role Requirements */}
          {app.jdRoleRequirements && app.jdRoleRequirements.length > 0 && (
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Role Requirements</h3>
              <ul className="space-y-1">
                {app.jdRoleRequirements.map((req, i) => (
                  <li key={i} className="text-sm text-gray-600 dark:text-gray-400 flex items-start gap-2">
                    <span className="text-gray-400 mt-0.5">&#8226;</span>
                    {req}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Experience Gaps */}
          {app.experienceGaps && app.experienceGaps.length > 0 && (
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Experience Gaps</h3>
              <div className="space-y-1">
                {app.experienceGaps.map((gap, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm text-yellow-700 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 rounded px-3 py-1.5">
                    <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    {gap}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          {app.notes && (
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Notes</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">{app.notes}</p>
            </div>
          )}

          {/* CTA to Full Page */}
          <div className="bg-indigo-50 dark:bg-indigo-900/10 border border-indigo-100 dark:border-indigo-900/50 rounded-xl p-6 text-center">
            <h3 className="text-indigo-900 dark:text-indigo-100 font-bold mb-2">Tailor This Content</h3>
            <p className="text-sm text-indigo-700 dark:text-indigo-300/80 mb-4">
              Use our AI editor to generate a professional, tailored resume and cover letter for this specific role.
            </p>
            <button
              onClick={() => app.id && onFullDetails(app.id)}
              className="px-6 py-3 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 shadow-lg shadow-indigo-500/20 transition-all"
            >
              Open Full Editor & AI Tools
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
